'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { apiGtm } from '@/api/gtm';
import { UiLegalConsentCheckbox } from '@/ui/legal-consent-checkbox';
import { UiKitButton } from '@/ui-kit/button';
import { useDialogContext } from '@/ui-kit/drawer';
import { UiKitInput } from '@/ui-kit/input';
import { UiKitToggle } from '@/ui-kit/toggle';
import { useForm } from '@/ui-tool/form';

import { registrationFormAction } from '../../api/registrationFormAction';
import { RegistrationHeader } from '../RegistrationHeader/RegistrationHeader';

import { styles } from './RegistrationForm.styles';

interface RegistrationFormProps {
  onSuccess: () => Promise<boolean>;
  onClose: () => void;
}

export function RegistrationForm({ onSuccess, onClose }: RegistrationFormProps) {
  const slots = styles();

  const t = useTranslations();
  const [isCompletingAuth, setIsCompletingAuth] = useState(false);
  const isSuccessHandledRef = useRef(false);

  const { contentRef } = useDialogContext();
  const { getFieldProps, pending, state, action } = useForm(
    registrationFormAction,
    {},
    { scrollRoot: () => contentRef }
  );

  const isSuccess = state.status === 'success';

  useEffect(() => {
    if (!isSuccess) {
      isSuccessHandledRef.current = false;
      return;
    }

    if (isSuccessHandledRef.current) return;
    isSuccessHandledRef.current = true;

    apiGtm.addContactInfo();

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsCompletingAuth(true);
    void onSuccess?.()
      .then((isSynced) => {
        if (!isSynced) {
          isSuccessHandledRef.current = false;
          setIsCompletingAuth(false);
        }
      })
      .catch(() => {
        isSuccessHandledRef.current = false;
        setIsCompletingAuth(false);
      });
  }, [isSuccess, onSuccess]);

  const [subscribeChecked, setSubscribeChecked] = useState(
    state.data?.subscribeToNewsletter === 'on'
  );
  const agreementFromServer =
    state.data?.agreement === true || state.data?.agreement === 'on';
  const restoreAgreement = state.status === 'error';

  return (
    <div className={slots.base()}>
      <RegistrationHeader />
      <form
        className={slots.base()}
        noValidate
        action={action}
      >
        <div>
          <div className={slots.fields()}>
            <UiKitInput
              label={t('registration.name')}
              placeholder={t('registration.name')}
              {...getFieldProps('name')}
            />
            <UiKitInput
              mask="99.99.9999"
              label={t('registration.birthDate')}
              placeholder={t('registration.birthDate')}
              {...getFieldProps('birthDate')}
            />
            <UiKitInput
              type="email"
              label="Email"
              placeholder="Email"
              {...getFieldProps('email')}
            />
          </div>
          <div>
            <input
              type="hidden"
              name="subscribeToNewsletter"
              value={subscribeChecked ? 'on' : ''}
            />
            <UiKitToggle
              className={slots.subscribe()}
              label={t('registration.subscribe')}
              checked={subscribeChecked}
              onChange={setSubscribeChecked}
            />
          </div>
        </div>
        <UiLegalConsentCheckbox
          key={
            restoreAgreement ? `agreement-${String(agreementFromServer)}` : 'agreement'
          }
          className={slots.agreement()}
          defaultChecked={restoreAgreement ? agreementFromServer : true}
          withAdsConsent={subscribeChecked}
          {...getFieldProps('agreement')}
        />
        <UiKitButton
          type="submit"
          className={slots.button()}
          disabled={pending || isCompletingAuth}
          loading={pending || isCompletingAuth}
        >
          {t('save')}
        </UiKitButton>
        <button
          type="button"
          className={slots.close()}
          onClick={onClose}
        >
          {t('registration.close')}
        </button>
      </form>
    </div>
  );
}
